import {
  V1EventState as EventState,
  type V1Event,
} from '@/generated/hooks/astral/models';
import type { V1CompletedRunStateOutputs } from '@/generated/hooks/bishop/models';
import {
  EAttachmentType,
  EMessageDirection,
  EMessageType,
  type IChatAttachment,
  type IChatMessage,
  type TAttachmentType,
} from '@kognitos/lattice';
import { getCurrentDate, parseTimestamp } from './datetime-utils';
import { parseResourceName } from './api-utils';
import { getSourceDisplayName } from './file-utils';
import Logo from '@/shared/components/Logo';
import i18next from 'i18next';

/**
 * Agent avatar URL for agent messages in chat.
 */
export const AGENT_AVATAR_URL = '/images/logo-yellow-on-black.png';

/**
 * Avatar fallback component for agent messages in chat.
 */
export const AGENT_AVATAR_FALLBACK = (
  <Logo type="default" className="text-primary" size={4} />
);

/**
 * Detects the attachment type based on the file extension.
 *
 * @param filename - The filename to detect the type for
 * @returns The attachment type for the file
 */
export function detectAttachmentType(filename: string): TAttachmentType {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return EAttachmentType.PDF;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return EAttachmentType.EXCEL;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'bmp':
      return EAttachmentType.IMAGE;
    case 'txt':
    case 'md':
    case 'json':
    case 'xml':
    case 'yaml':
    case 'yml':
      return EAttachmentType.TEXT;
    default:
      return EAttachmentType.FILE;
  }
}

/**
 * Extracts a human-readable display name from a file resource path.
 *
 * Handles the full Bishop resource path format:
 *   organizations/{org}/files/{fileId}
 *   organizations/{org}/workspaces/{ws}/files/{fileId}
 *
 * Strips the storage ID prefix (e.g. "abc123--") from the filename segment
 * so that only the human-readable part is returned.
 *
 * @param resourceName - The full resource path or bare filename
 * @returns Human-readable display name
 */
export function extractFilenameFromResource(resourceName: string): string {
  return getSourceDisplayName(resourceName);
}

/**
 * Constructs an agent chat message.
 * @param id - The event ID
 * @param content - The content of the message
 * @param timestamp - The timestamp of the message
 * @returns The IChatMessage
 *
 * @example
 * constructAgentChatMessage('event-1', 'Hello, how are you?', new Date('2025-01-01T12:00:00Z'))
 * // returns {
 * //   id: 'event-1',
 * //   content: 'Hello, how are you?',
 * //   timestamp: new Date('2025-01-01T12:00:00Z'),
 * //   direction: EMessageDirection.INCOMING,
 * //   avatarUrl: AGENT_AVATAR_URL,
 * //   avatarFallback: AGENT_AVATAR_FALLBACK,
 * // }
 */
export const constructAgentChatMessage = (
  id: string,
  content: string,
  timestamp: Date,
): IChatMessage => {
  return {
    id,
    content,
    timestamp,
    direction: EMessageDirection.INCOMING,
    avatarUrl: AGENT_AVATAR_URL,
    avatarFallback: AGENT_AVATAR_FALLBACK,
  };
};

/**
 * Converts a V1Event from the Astral API to an IChatMessage for the chat component.
 *
 * Supports both simple content format and content_list format with files.
 *
 * @param event - The V1Event to convert
 * @param userImage - Optional user image URL for user messages
 * @param userInitials - Optional user initials for user message avatar fallback
 * @returns IChatMessage or null if the event type is not supported
 */
export const convertEventToChatMessage = (
  event: V1Event,
  userImage?: string,
  userInitials?: string,
): IChatMessage | null => {
  const eventId = parseResourceName(event.name).event || `event-${Date.now()}`;
  const timestamp = event.create_time
    ? parseTimestamp(event.create_time)
    : getCurrentDate();

  // Handle user messages (outgoing)
  if (event.user_message) {
    // Handle content_list format (may include files)
    if (event.user_message.content_list?.items) {
      const textParts: string[] = [];
      const attachments: IChatAttachment[] = [];

      for (const item of event.user_message.content_list.items) {
        if (item.text) {
          textParts.push(item.text);
        }
        if (item.file?.remote) {
          const filename = extractFilenameFromResource(item.file.remote);
          attachments.push({
            id: item.file.remote,
            name: filename,
            type: detectAttachmentType(filename),
          });
        }
      }

      const content = textParts.join('\n').trim();

      // Don't show user messages with no content and no attachments
      if (!content && attachments.length === 0) {
        return null;
      }

      return {
        id: eventId,
        content: content || undefined,
        direction: EMessageDirection.OUTGOING,
        timestamp,
        avatarUrl: userImage,
        avatarFallback: userInitials?.[0] || 'U',
        attachments: attachments.length > 0 ? attachments : undefined,
      };
    }

    // Handle simple content format (backward compatibility)
    const content = event.user_message.content || '';
    // Don't show user messages with empty content
    if (!content.trim()) {
      return null;
    }
    return {
      id: eventId,
      content,
      direction: EMessageDirection.OUTGOING,
      timestamp,
      avatarUrl: userImage,
      avatarFallback: userInitials?.[0] || 'U',
    };
  }

  // Handle agent messages (incoming)
  if (event.agent_message && event.agent_message.content) {
    return constructAgentChatMessage(
      eventId,
      event.agent_message.content,
      timestamp,
    );
  }

  // Handle completion_response with error (show as agent message)
  if (event.completion_response?.error) {
    return constructAgentChatMessage(
      eventId,
      event.completion_response.error,
      timestamp,
    );
  }

  // Tool calls, thinking, and other event types are not displayed as chat messages
  // They could be shown as status indicators or in a separate panel
  return null;
};

/**
 * Gets the thinking status text from the translation file.
 *
 * @returns Thinking status text
 */
export const getThinkingStatusText = (): string => {
  return i18next.t('agent.chat.thinking', { ns: 'agent' });
};

/**
 * Converts a V1Event to a status text for chat display.
 *
 * @deprecated Use convertEventToThinkingMessage instead to add thinking messages
 * to the messages array. The Chat component will automatically group and display them.
 *
 * @param event - The V1Event to convert
 * @returns Status text string or null if no status text should be displayed
 */
export const convertEventToChatStatusText = (event: V1Event): string | null => {
  if (event.state !== EventState.STATE_STREAMING) {
    return null;
  }

  let content = null;
  // we can handle the tool call requests status updates separately in the future
  if (event.thinking || event.tool_call_request) {
    content = getThinkingStatusText();
  }

  return content || null;
};

/**
 * Converts a V1Event with thinking content to a thinking-type IChatMessage.
 *
 * Thinking messages are displayed in a collapsible box by the Chat component.
 * Consecutive thinking messages are automatically grouped together.
 *
 * @param event - The V1Event to convert (must have thinking content)
 * @param options - Optional configuration
 * @param options.streamingOnly - If true, only convert streaming events (default: false)
 * @returns IChatMessage with type='thinking' or null if not a thinking event
 *
 * @example
 * // For historical events from listEvents (convert all thinking events)
 * const thinkingMessage = convertEventToThinkingMessage(event);
 *
 * // For real-time streaming (only convert streaming events)
 * const thinkingMessage = convertEventToThinkingMessage(event, { streamingOnly: true });
 */
export const convertEventToThinkingMessage = (
  event: V1Event,
  options?: { streamingOnly?: boolean },
): IChatMessage | null => {
  const { streamingOnly = false } = options || {};

  // If streamingOnly is true, only convert events that are still streaming
  if (streamingOnly && event.state !== EventState.STATE_STREAMING) {
    return null;
  }

  // Handle thinking events
  if (event.thinking?.content) {
    const eventId =
      parseResourceName(event.name).event || `thinking-${Date.now()}`;
    const timestamp = event.create_time
      ? parseTimestamp(event.create_time)
      : getCurrentDate();

    return {
      id: eventId,
      content: event.thinking.content,
      direction: EMessageDirection.INCOMING,
      timestamp,
      type: EMessageType.THINKING,
      avatarUrl: AGENT_AVATAR_URL,
      avatarFallback: AGENT_AVATAR_FALLBACK,
    };
  }

  return null;
};

/**
 * A parsed fact from related outputs XML.
 */
export interface IParsedFact {
  /** Field name */
  field: string;
  /** Data type (text, number, boolean, table, json, select) */
  type: string;
  /** Extraction status */
  status: 'missing' | 'present';
  /** Value if present, undefined if missing */
  value?: string;
  /** Options for select type fields (comma-separated in XML, parsed to array) */
  options?: string[];
  /** Page number (1-indexed) within the document where this fact was extracted */
  page_number?: number;
  /** Extraction confidence score for this fact */
  confidence?: number;
  /** Bounding box for highlighting the fact on the document page */
  bounding_box?: { x: number; y: number; width: number; height: number };
}

/**
 * Source information for a group of facts.
 */
export interface IParsedFactSource {
  /** Source name (file resource name for documents, descriptive name for external systems) */
  name?: string;
  /** Page number (1-indexed) for document sources */
  page?: number;
}

/**
 * A group of facts from a single source.
 */
export interface IParsedFactGroup {
  /** Optional source information */
  source?: IParsedFactSource;
  /** Array of facts from this source */
  facts: IParsedFact[];
}

/**
 * Context types for related_outputs.
 */
export type TRelatedOutputsContext =
  | 'need_information'
  | 'analyze_outputs'
  | 'manual_action_required';

/**
 * Result of parsing related outputs from message content.
 */
export interface IParseRelatedOutputsResult {
  /** The message content with related_outputs XML removed */
  cleanedContent: string;
  /** Related outputs in V1CompletedRunStateOutputs format, or null if none found */
  relatedOutputs: V1CompletedRunStateOutputs | null;
  /**
   * Fact groups with source attribution (new format).
   * Each group contains facts from a single source.
   */
  factGroups: IParsedFactGroup[] | null;
  /**
   * @deprecated Use factGroups instead. Kept for backward compatibility.
   * Flat array of all parsed facts (flattened from factGroups).
   */
  facts: IParsedFact[] | null;
  /**
   * @deprecated Use factGroups[].source.name instead. Kept for backward compatibility.
   * Source from top-level source attribute.
   */
  source: string | null;
  /** Context type from the related_outputs tag (need_information, analyze_outputs, manual_action_required) */
  context: TRelatedOutputsContext | null;
}

/**
 * Unescapes XML entities in a string.
 *
 * @param text - The text with XML entities to unescape
 * @returns Text with XML entities converted back to characters
 */
const unescapeXml = (text: string): string => {
  return text
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&'); // Must be last to avoid double-unescaping
};

/**
 * Extracts and parses a numeric attribute from an XML element string.
 *
 * @param element - The XML element string to search
 * @param attrName - The attribute name to extract
 * @param parser - The numeric parser function (parseInt or parseFloat)
 * @returns The parsed number, or undefined if not found or invalid
 */
const matchNumericAttr = (
  element: string,
  attrName: string,
  parser: (value: string, radix?: number) => number,
): number | undefined => {
  const match = element.match(new RegExp(`${attrName}=["']([^"']*)["']`));
  if (!match) return undefined;
  const parsed = parser(match[1], 10);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * Extracts and parses a JSON attribute from an XML element string.
 * The attribute value is expected to be XML-escaped JSON.
 *
 * @param element - The XML element string to search
 * @param attrName - The attribute name to extract
 * @returns The parsed JSON object, or undefined if not found or invalid
 */
const matchJsonAttr = <T,>(
  element: string,
  attrName: string,
): T | undefined => {
  const match = element.match(new RegExp(`${attrName}=["']([^"']*)["']`));
  if (!match) return undefined;
  try {
    return JSON.parse(unescapeXml(match[1]));
  } catch {
    return undefined;
  }
};

/**
 * Parses a single <fact> element and extracts its attributes and value.
 *
 * @param factElement - The fact element string to parse
 * @returns Parsed fact object or null if parsing fails
 */
const parseFact = (factElement: string): IParsedFact | null => {
  // Match fact element with attributes: <fact field="..." type="..." status="...">value</fact>
  // or self-closing: <fact field="..." type="..." status="..." />
  const attrRegex =
    /field=["']([^"']*)["']\s+type=["']([^"']*)["']\s+status=["']([^"']*)["']/;
  const attrMatch = factElement.match(attrRegex);

  if (!attrMatch) {
    return null;
  }

  const field = unescapeXml(attrMatch[1]);
  const type = unescapeXml(attrMatch[2]);
  const status = attrMatch[3] as 'present' | 'missing';

  // Extract value content between tags (if not self-closing)
  const valueRegex = />([^<]*)</;
  const valueMatch = factElement.match(valueRegex);
  // Unescape XML entities in value (e.g., &quot; -> ", &amp; -> &)
  const value = valueMatch ? unescapeXml(valueMatch[1]) : undefined;

  // Extract options attribute for select type (pipe-separated, XML-escaped)
  const optionsMatch = factElement.match(/options=["']([^"']*)["']/);
  const options = optionsMatch
    ? optionsMatch[1].split('|').map(o => unescapeXml(o.trim()))
    : undefined;

  // Extract optional numeric and JSON attributes
  const page_number = matchNumericAttr(factElement, 'page_number', parseInt);
  const confidence = matchNumericAttr(factElement, 'confidence', parseFloat);
  const bounding_box = matchJsonAttr<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>(factElement, 'bounding_box');

  return {
    field,
    type,
    status,
    value,
    ...(options && { options }),
    ...(page_number !== undefined && { page_number }),
    ...(confidence !== undefined && { confidence }),
    ...(bounding_box && { bounding_box }),
  };
};

/**
 * Converts a parsed fact to a Commonv1Value format.
 *
 * @param fact - The parsed fact to convert
 * @returns The value in Commonv1Value format
 */
const factToCommonValue = (
  fact: IParsedFact,
): V1CompletedRunStateOutputs[string] => {
  if (fact.status === 'missing' || fact.value === undefined) {
    return { null_value: 'NULL' };
  }

  // For now, treat all values as text. The actual type conversion
  // can be enhanced based on the 'type' attribute if needed.
  return { text: fact.value };
};

/**
 * Parses a <facts> element and returns a fact group with source and facts.
 *
 * @param factsElement - The facts element string to parse
 * @returns Parsed fact group or null if no facts found
 */
const parseFactsGroup = (factsElement: string): IParsedFactGroup | null => {
  // Extract source and page attributes from <facts source="..." page="...">
  const sourceMatch = factsElement.match(/source=["']([^"']*)["']/);
  const pageMatch = factsElement.match(/page=["']([^"']*)["']/);

  const sourceName = sourceMatch ? sourceMatch[1] : undefined;
  const pageParsed = pageMatch ? parseInt(pageMatch[1], 10) : undefined;
  const page = pageParsed && !isNaN(pageParsed) ? pageParsed : undefined;

  // Parse all <fact> elements within this <facts> group
  const factRegex = /<fact\s+[^>]*(?:\/>|>[^<]*<\/fact>)/g;
  const factMatches = factsElement.match(factRegex);

  if (!factMatches || factMatches.length === 0) {
    return null;
  }

  const facts: IParsedFact[] = [];
  for (const factElement of factMatches) {
    const parsedFact = parseFact(factElement);
    if (parsedFact) {
      facts.push(parsedFact);
    }
  }

  if (facts.length === 0) {
    return null;
  }

  // Build source object only if we have meaningful data
  const source: IParsedFactSource | undefined =
    sourceName || page !== undefined ? { name: sourceName, page } : undefined;

  return { source, facts };
};

/**
 * Parses related_outputs XML from message content and returns extracted outputs and cleaned content.
 *
 * Supports two formats:
 *
 * New format (with fact groups):
 * ```xml
 * <related_outputs context="need_information">
 *   <facts source="invoice.pdf" page="1">
 *     <fact field="merchant_name" type="text" status="present">Lyft</fact>
 *     <fact field="expense_date" type="text" status="missing" />
 *   </facts>
 *   <facts source="receipt.pdf" page="2">
 *     <fact field="total" type="number" status="present">100</fact>
 *   </facts>
 * </related_outputs>
 * ```
 *
 * Legacy format (flat facts, backward compatible):
 * ```xml
 * <related_outputs source="invoice.pdf">
 *   <fact field="merchant_name" type="text" status="present">Lyft</fact>
 *   <fact field="expense_date" type="text" status="missing" />
 * </related_outputs>
 * ```
 *
 * @param content - The message content that may contain related_outputs XML
 * @returns Object containing cleaned content, related outputs, factGroups, facts, and source
 */
export const parseRelatedOutputs = (
  content: string,
): IParseRelatedOutputsResult => {
  // Match <related_outputs> with optional attributes
  const regex = /<related_outputs([^>]*)>([\s\S]*?)<\/related_outputs>/;
  const match = content.match(regex);

  if (!match) {
    return {
      cleanedContent: content,
      relatedOutputs: null,
      factGroups: null,
      facts: null,
      source: null,
      context: null,
    };
  }

  const attributes = match[1];
  const innerContent = match[2];

  // Parse source attribute from <related_outputs source="...">
  const sourceMatch = attributes.match(/source=["']([^"']*)["']/);
  const sourceAttr = sourceMatch ? sourceMatch[1] : null;

  // Parse context attribute from <related_outputs context="...">
  const contextMatch = attributes.match(/context=["']([^"']*)["']/);
  const context = contextMatch
    ? (contextMatch[1] as TRelatedOutputsContext)
    : null;

  // Source comes from the source attribute
  const source = sourceAttr;

  // Try to parse nested <facts> elements (new format)
  const factsGroupRegex = /<facts([^>]*)>([\s\S]*?)<\/facts>/g;
  const factsGroupMatches = innerContent.match(factsGroupRegex);

  const relatedOutputs: V1CompletedRunStateOutputs = {};
  const factGroups: IParsedFactGroup[] = [];
  const allFacts: IParsedFact[] = [];

  if (factsGroupMatches && factsGroupMatches.length > 0) {
    // New format: parse each <facts> group
    for (const factsGroupElement of factsGroupMatches) {
      const group = parseFactsGroup(factsGroupElement);
      if (group) {
        factGroups.push(group);
        // Add facts to relatedOutputs and allFacts for backward compatibility
        for (const fact of group.facts) {
          relatedOutputs[fact.field] = factToCommonValue(fact);
          allFacts.push(fact);
        }
      }
    }
  } else {
    // Legacy format: parse flat <fact> elements directly under <related_outputs>
    const factRegex = /<fact\s+[^>]*(?:\/>|>[^<]*<\/fact>)/g;
    const factMatches = innerContent.match(factRegex);

    if (factMatches && factMatches.length > 0) {
      const legacyFacts: IParsedFact[] = [];
      for (const factElement of factMatches) {
        const parsedFact = parseFact(factElement);
        if (parsedFact) {
          relatedOutputs[parsedFact.field] = factToCommonValue(parsedFact);
          legacyFacts.push(parsedFact);
          allFacts.push(parsedFact);
        }
      }

      // Create a single fact group with legacy source info
      if (legacyFacts.length > 0) {
        const legacySource: IParsedFactSource | undefined = source
          ? { name: source }
          : undefined;
        factGroups.push({ source: legacySource, facts: legacyFacts });
      }
    }
  }

  const cleanedContent = content.replace(regex, '').trim();

  // Return empty arrays as null for backward compatibility
  const hasFactGroups = factGroups.length > 0;
  const hasFacts = allFacts.length > 0;

  // For backward compatibility: if no top-level source, use the first fact group's source
  // This ensures the IDP preview can still be shown for the new format
  const effectiveSource =
    source || (hasFactGroups ? factGroups[0].source?.name : null) || null;

  return {
    cleanedContent,
    relatedOutputs:
      Object.keys(relatedOutputs).length > 0 ? relatedOutputs : null,
    factGroups: hasFactGroups ? factGroups : null,
    facts: hasFacts ? allFacts : null,
    source: effectiveSource,
    context,
  };
};

/**
 * Parsed guide entry data from XML.
 */
export interface IParsedGuideEntry {
  /** Action type: 'create', 'update', or 'use' */
  action: 'create' | 'update' | 'use';
  /** Full resource name of the guide entry */
  name: string;
  /** Title of the guide entry */
  title: string;
  /** Root cause analysis */
  rootCause: string;
  /** Resolution steps/instructions (new field) */
  resolutionSteps?: string;
  /** @deprecated Use resolutionSteps instead. Resolution content/instructions (legacy field for backward compatibility) */
  content?: string;
  /** Current state (e.g., 'STATE_PENDING_APPROVAL') */
  state: string;
  /** Version number */
  version: string;
}

/**
 * Result of parsing guide_entry XML from message content.
 */
export interface IParseGuideEntryResult {
  /** Content with guide_entry XML removed */
  cleanedContent: string;
  /** Parsed guide entry data, or null if no valid XML found */
  guideEntry: IParsedGuideEntry | null;
}

/**
 * Parses guide_entry XML from message content and returns extracted data and cleaned content.
 *
 * Expected format:
 * ```xml
 * <guide_entry action="create">
 *   <name>organizations/.../guideEntries/abc123</name>
 *   <title>Missing PO number - SAP lookup</title>
 *   <root_cause>PO number not extractable from invoice</root_cause>
 *   <resolution_steps>When PO number is missing: 1. Look up in SAP...</resolution_steps>
 *   <state>STATE_PENDING_APPROVAL</state>
 *   <version>1</version>
 * </guide_entry>
 * ```
 *
 * @param content - The message content that may contain guide_entry XML
 * @returns Object containing cleaned content and parsed guide entry
 */
export const parseGuideEntryXml = (content: string): IParseGuideEntryResult => {
  const regex = /<guide_entry([^>]*)>([\s\S]*?)<\/guide_entry>/;
  const match = content.match(regex);

  if (!match) {
    return {
      cleanedContent: content,
      guideEntry: null,
    };
  }

  const attributes = match[1];
  const innerContent = match[2];

  // Parse action attribute
  const actionMatch = attributes.match(/action=["']([^"']*)["']/);
  const action = (actionMatch?.[1] as 'create' | 'update' | 'use') || 'create';

  // Helper to extract tag content
  const extractTag = (tagName: string): string => {
    const tagRegex = new RegExp(`<${tagName}>([\\s\\S]*?)<\\/${tagName}>`);
    const tagMatch = innerContent.match(tagRegex);
    return tagMatch?.[1]?.trim() || '';
  };

  // Unescape XML entities
  const unescapeXml = (text: string): string => {
    return text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&amp;/g, '&');
  };

  // Extract both resolution_steps (new) and content (legacy) for backward compatibility
  const resolutionSteps = unescapeXml(extractTag('resolution_steps'));
  const legacyContent = unescapeXml(extractTag('content'));

  const guideEntry: IParsedGuideEntry = {
    action,
    name: unescapeXml(extractTag('name')),
    title: unescapeXml(extractTag('title')),
    rootCause: unescapeXml(extractTag('root_cause')),
    resolutionSteps: resolutionSteps || undefined,
    content: legacyContent || undefined,
    state: extractTag('state'),
    version: extractTag('version'),
  };

  // Only return guide entry if we have required fields
  if (!guideEntry.name || !guideEntry.title) {
    return {
      cleanedContent: content,
      guideEntry: null,
    };
  }

  const cleanedContent = content.replace(regex, '').trim();

  return {
    cleanedContent,
    guideEntry,
  };
};

/**
 * A single follow-up suggestion parsed from follow_up_suggestions XML.
 */
export interface IParsedFollowUpSuggestion {
  /** Chip text shown to the user. */
  label: string;
  /** Message sent to the agent when the suggestion is clicked. */
  message: string;
}

/**
 * Result of parsing follow_up_suggestions XML from message content.
 */
export interface IParseFollowUpSuggestionsResult {
  /** Content with the follow_up_suggestions XML removed. */
  cleanedContent: string;
  /** Parsed suggestions (empty when no valid block is found). */
  suggestions: IParsedFollowUpSuggestion[];
}

/**
 * Parses follow_up_suggestions XML from message content and returns the
 * extracted suggestions plus the content with the block removed.
 *
 * Astral's `suggest_follow_ups` tool emits this block as an agent message.
 * Each suggestion's `message` attribute is the text sent to the agent on
 * click; the element's inner text is the chip label. The `message` attribute
 * is optional and falls back to the label.
 *
 * Expected format:
 * ```xml
 * <follow_up_suggestions>
 *   <suggestion message="Show me the run details">Show run details</suggestion>
 *   <suggestion message="Retry the automation">Retry automation</suggestion>
 * </follow_up_suggestions>
 * ```
 *
 * @param content - The message content that may contain follow_up_suggestions XML
 * @returns Object containing cleaned content and parsed suggestions
 */
export const parseFollowUpSuggestions = (
  content: string,
): IParseFollowUpSuggestionsResult => {
  const regex =
    /<follow_up_suggestions[^>]*>([\s\S]*?)<\/follow_up_suggestions>/;
  const match = content.match(regex);

  if (!match) {
    return { cleanedContent: content, suggestions: [] };
  }

  const innerContent = match[1];
  const suggestionRegex = /<suggestion([^>]*)>([\s\S]*?)<\/suggestion>/g;
  const suggestions: IParsedFollowUpSuggestion[] = [];

  let suggestionMatch: RegExpExecArray | null;
  while ((suggestionMatch = suggestionRegex.exec(innerContent)) !== null) {
    const attributes = suggestionMatch[1];
    const label = unescapeXml(suggestionMatch[2].trim());

    // Skip empty labels (malformed XML)
    if (!label) {
      continue;
    }

    const messageMatch = attributes.match(/\bmessage=["']([^"']*)["']/);
    const message = messageMatch ? unescapeXml(messageMatch[1]) : label;

    suggestions.push({ label, message: message || label });
  }

  // Only strip the block from content when it yielded usable suggestions.
  if (suggestions.length === 0) {
    return { cleanedContent: content, suggestions: [] };
  }

  return {
    cleanedContent: content.replace(regex, '').trim(),
    suggestions,
  };
};
